import { Lock } from 'iconoir-react';
import React, { FunctionComponent, ReactElement, Ref } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/shared';
import { MessageType } from '@savgroup-front-common/types';

import { safeFormattedIntlString } from '../../formatters';
import { SUPPORTED_ICON_POSITIONS } from '../../helpers/theme';

import { useHumanLoadingButton } from './Button.hooks';
import {
  $Count,
  $InnerButtonStyled,
  $LockedContent,
  $LockedTooltip,
} from './Button.styles';
import { Icon } from './Icon';
import LoadingBasedOnProps from './LoadingBasedOnProps';

export interface ButtonComponentProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
  isError?: boolean;
  disabled?: boolean;
  dataTestId?: string;
  small?: boolean;
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  success?: boolean;
  danger?: boolean;
  alert?: boolean;
  hollow?: boolean;
  rounded?: boolean;
  underline?: boolean;
  fluid?: boolean;
  type?: BUTTON_TYPES;
  componentThemeName?: string;
  naked?: boolean;
  tabIndex?: number | undefined;
  isPressed?: boolean;
  icon?: ReactElement;
  position?: SUPPORTED_ICON_POSITIONS;
  isSuffix?: boolean;
  count?: number;
  isMinimal?: boolean;
  ariaLabel?: MessageType;
  isLocked?: boolean;
  lockedMessage?: MessageType;
  isActive?: boolean;
}

export interface ButtonProps extends ButtonComponentProps {
  forwardedRef: Ref<HTMLButtonElement>;
}

const Button: FunctionComponent<React.PropsWithChildren<ButtonProps>> = ({
  onClick = () => null,
  isLoading = false,
  isError = false,
  children,
  disabled = false,
  dataTestId,
  primary = false,
  secondary = false,
  tertiary = false,
  success = false,
  danger = false,
  alert = false,
  rounded = false,
  hollow = false,
  underline = false,
  fluid = false,
  type = BUTTON_TYPES.SUBMIT,
  forwardedRef,
  naked = false,
  isPressed = false,
  icon,
  position = SUPPORTED_ICON_POSITIONS.LEFT,
  componentThemeName,
  isSuffix,
  onMouseDown,
  onMouseLeave,
  onMouseUp,
  onTouchEnd,
  onTouchStart,
  count = 0,
  isMinimal,
  ariaLabel,
  isLocked,
  lockedMessage,
  isActive = false,
  ...rest
}) => {
  const isHumanLoading = useHumanLoadingButton({ isLoading });
  const isSquareIconButton = Boolean(icon && children === undefined);
  const isSecondaryStyle = secondary;
  const isTertiaryStyle = tertiary || (isSecondaryStyle && hollow);

  return (
    <$InnerButtonStyled
      {...rest}
      position={children ? position : undefined}
      componentThemeName={componentThemeName}
      naked={naked}
      type={type}
      primary={primary}
      secondary={isSecondaryStyle && !isTertiaryStyle}
      tertiary={isTertiaryStyle}
      success={success}
      danger={danger}
      alert={alert}
      rounded={rounded}
      hollow={hollow}
      underline={underline}
      fluid={fluid}
      isError={isError}
      data-testid={dataTestId}
      isLoading={isHumanLoading}
      ref={forwardedRef}
      disabled={isLoading || disabled || isLocked}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
          onClick(e);
        }
      }}
      $isPressed={isPressed}
      $isSquareIconButton={isSquareIconButton}
      $isSuffix={isSuffix}
      $isIcon={icon !== undefined}
      $isOnlyIcon={!!children && icon !== undefined}
      $isMinimal={isMinimal}
      aria-label={ariaLabel ? safeFormattedIntlString(ariaLabel) : undefined}
      $isActive={isActive}
    >
      <LoadingBasedOnProps
        disabled={disabled}
        isLoading={isHumanLoading}
        secondary={secondary}
        primary={primary}
        tertiary={tertiary}
        alert={alert}
        danger={danger}
        success={success}
      >
        {icon === undefined && !isLocked && (children || '\u00A0')}
        {icon && !isLocked && (
          <Icon
            icon={icon}
            position={children ? position : undefined}
            componentThemeName={componentThemeName}
            tertiary={tertiary}
            secondary={secondary}
            disabled={disabled}
          >
            {children}
          </Icon>
        )}
        {isLocked && (
          <$LockedTooltip content={safeFormattedIntlString(lockedMessage)}>
            <$LockedContent>
              <Lock width={16} height={16} />
              {children}
            </$LockedContent>
          </$LockedTooltip>
        )}
        {count > 0 && (
          <$Count
            secondary={secondary}
            primary={primary}
            tertiary={tertiary}
            alert={alert}
            danger={danger}
            success={success}
          >
            {count}
          </$Count>
        )}
      </LoadingBasedOnProps>
    </$InnerButtonStyled>
  );
};

export default React.forwardRef<HTMLButtonElement, ButtonComponentProps>(
  (props, ref) => <Button forwardedRef={ref} {...props} />,
);
